/**
 * Ответы приложений в ленте (ADR 0010): кто вызвал команду, «только вам», «думает…», кнопки
 * и списки под сообщением. Чистые функции без React и сторов — проверяются в
 * scripts/commands.test.mjs.
 */
import { ButtonStyle, ComponentType, InteractionType, MessageFlags, type ActionRow, type ButtonComponent, type Message, type SelectComponent, type User } from '@/types/api'
import type { PickKind } from './commands'

export const isEphemeral = (m: Pick<Message, 'flags'>) => ((m.flags ?? 0) & MessageFlags.Ephemeral) !== 0
export const isLoading = (m: Pick<Message, 'flags'>) => ((m.flags ?? 0) & MessageFlags.Loading) !== 0

/** Токен взаимодействия живёт 15 минут: кто «думает» дольше, уже не ответит. */
export const RESPONSE_WINDOW_MS = 15 * 60 * 1000

export function responseExpired(m: Pick<Message, 'timestamp'>, now = Date.now()): boolean {
  return now - new Date(m.timestamp).getTime() > RESPONSE_WINDOW_MS
}

/**
 * Строка над ответом на слэш-команду: кто вызвал и что. Имя с подкомандой есть только в старом
 * interaction — его и берём; у ответов на кнопки строки нет. Нет её и у дополнительных сообщений
 * (followup — у них есть original_response_message_id): как у Discord, строка только над первым
 * ответом, иначе при нескольких сообщениях бота она повторялась бы над каждым.
 */
export function commandInvocation(m: Pick<Message, 'interaction' | 'interaction_metadata'>): { user: User; name: string } | null {
  const meta = m.interaction_metadata
  const legacy = m.interaction
  if ((meta?.type ?? legacy?.type) !== InteractionType.ApplicationCommand) return null
  if (meta?.original_response_message_id) return null
  const user = meta?.user ?? legacy?.user
  if (!user) return null
  return { user, name: legacy?.name ?? meta?.name ?? '' }
}

/** Сравнение snowflake как чисел произвольной длины (то же, что compareIds в сторе). */
function compareIds(a: string, b: string): number {
  if (a.length !== b.length) return a.length - b.length
  return a < b ? -1 : a > b ? 1 : 0
}

/**
 * Лента канала и сообщения «только вам» — одним списком по порядку id. Отправляемые
 * и не отправленные (у них нет настоящего id) остаются в самом конце, как и были.
 * Без сообщений «только вам» возвращает тот же массив: эффекты от него не перезапускаются.
 */
export function mergeEphemeral(list: readonly Message[], ephemeral: readonly Message[] | undefined): readonly Message[] {
  if (!ephemeral?.length) return list
  const merged = list.filter((m) => !m.pending && !m.failed)
  const tail = list.filter((m) => m.pending || m.failed)
  const seen = new Set(merged.map((m) => m.id))
  for (const e of [...ephemeral].sort((a, b) => compareIds(a.id, b.id))) {
    if (seen.has(e.id)) continue
    const at = merged.findIndex((m) => compareIds(m.id, e.id) > 0)
    if (at === -1) merged.push(e)
    else merged.splice(at, 0, e)
  }
  return [...merged, ...tail]
}

/** Адрес кнопки-ссылки: только http и https — javascript: и прочее не открываем. */
export function safeLinkUrl(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const u = new URL(url)
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null
  } catch {
    return null
  }
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

const SELECT_TYPES = new Set<number>([ComponentType.StringSelect, ComponentType.UserSelect, ComponentType.RoleSelect, ComponentType.MentionableSelect, ComponentType.ChannelSelect])

function isButton(c: Record<string, unknown>): boolean {
  if (c.type !== ComponentType.Button || typeof c.style !== 'number') return false
  if (c.style === ButtonStyle.Link) return typeof c.url === 'string'
  return c.style >= ButtonStyle.Primary && c.style <= ButtonStyle.Danger && typeof c.custom_id === 'string'
}

function isSelect(c: Record<string, unknown>): boolean {
  if (typeof c.type !== 'number' || !SELECT_TYPES.has(c.type) || typeof c.custom_id !== 'string') return false
  return c.type !== ComponentType.StringSelect || Array.isArray(c.options)
}

/**
 * Строки кнопок и списков, которые умеем показать: до 5 строк, в строке — кнопки или список.
 * Незнакомое (платные кнопки, Components V2, поля модалок) пропускаем, а не падаем на нём.
 */
export function actionRows(components: readonly unknown[] | null | undefined): ActionRow[] {
  if (!Array.isArray(components)) return []
  const rows: ActionRow[] = []
  for (const row of components) {
    if (!isObject(row) || row.type !== ComponentType.ActionRow || !Array.isArray(row.components)) continue
    const items = (row.components as unknown[]).filter((c): c is ButtonComponent | SelectComponent => isObject(c) && (isButton(c) || isSelect(c)))
    if (items.length) rows.push({ type: ComponentType.ActionRow, components: items })
  }
  return rows.slice(0, 5)
}

/** Какой список: у «своих вариантов» (3) — null, остальные выбирают из участников, ролей, каналов. */
export function selectKind(type: number): PickKind | null {
  switch (type) {
    case ComponentType.UserSelect:
      return 'user'
    case ComponentType.RoleSelect:
      return 'role'
    case ComponentType.MentionableSelect:
      return 'mentionable'
    case ComponentType.ChannelSelect:
      return 'channel'
    default:
      return null
  }
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n))

/** Сколько значений можно выбрать: по умолчанию ровно одно, как у Discord; не больше, чем вариантов. */
export function selectLimits(c: Pick<SelectComponent, 'type' | 'options' | 'min_values' | 'max_values'>): { min: number; max: number } {
  const count = c.type === ComponentType.StringSelect ? (c.options?.length ?? 0) : 25
  const max = clamp(Math.min(c.max_values ?? 1, Math.max(count, 1)), 1, 25)
  const min = clamp(c.min_values ?? 1, 0, max)
  return { min, max }
}

/** Выбор можно отправить: без повторов, в пределах min/max и (у своих вариантов) только из них. */
export function selectionAllowed(c: Pick<SelectComponent, 'type' | 'options' | 'min_values' | 'max_values'>, values: readonly string[]): boolean {
  const { min, max } = selectLimits(c)
  if (values.length < min || values.length > max || new Set(values).size !== values.length) return false
  if (c.type !== ComponentType.StringSelect) return true
  const allowed = new Set((c.options ?? []).map((o) => o.value))
  return values.every((v) => allowed.has(v))
}

/** Выбранное заранее у своих вариантов (default: true), не больше, чем можно. */
export function selectDefaults(c: Pick<SelectComponent, 'type' | 'options' | 'min_values' | 'max_values'>): string[] {
  if (c.type !== ComponentType.StringSelect) return []
  return (c.options ?? [])
    .filter((o) => o.default)
    .map((o) => o.value)
    .slice(0, selectLimits(c).max)
}

export type ButtonTone = 'primary' | 'secondary' | 'success' | 'danger' | 'link'

export function buttonTone(style: number): ButtonTone {
  switch (style) {
    case ButtonStyle.Primary:
      return 'primary'
    case ButtonStyle.Success:
      return 'success'
    case ButtonStyle.Danger:
      return 'danger'
    case ButtonStyle.Link:
      return 'link'
    default:
      return 'secondary'
  }
}
